"use client";

import React, { useContext, useState } from "react";
import { Brush, Calendar, Code, Edit3, Layers, Settings, Sparkles, Star, UserCheck, Users } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import { LandingRobot } from "@/images";
import { AppContext } from "@/lib/contexts";
import { cn } from "@/lib/utils";
import { AppReducerActionType } from "@/types";
import { CTABanner, PricingSection } from "./LandingSections";
import LazyLoadSection from "./LazyLoadSection";

// Updated fallback component with a skeleton loader
const SectionFallback: React.FC = () => {
  return (
    // Give it a reasonable height to prevent layout shift
    // Adjust min-h value based on typical section heights
    <div className="w-full min-h-[400px] md:min-h-[500px] flex items-center justify-center py-20 bg-slate-900/50" data-sentry-component="SectionFallback" data-sentry-source-file="LandingContent.tsx">
            {/* Simple pulsing skeleton box */}
            <div className="w-3/4 h-64 bg-slate-800/70 rounded-lg animate-pulse"></div>
            {/* Or use the spinner if preferred */}
            {/* <Loader2 className="h-10 w-10 animate-spin text-blue-500" /> */}
        </div>
  );
};

// Convert React.lazy to next/dynamic
const ImageToImageSection = dynamic(() => import("./sections/ImageToImageSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const FeaturesCardGrid = dynamic(() => import("./sections/FeaturesCardGrid"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const KeyFeaturesSection = dynamic(() => import("./sections/KeyFeaturesSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const RewardsSection = dynamic(() => import("./sections/RewardsSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const AIModelsSection = dynamic(() => import("./sections/AIModelsSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const CommunitySection = dynamic(() => import("./sections/CommunitySection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const TestimonialsSection = dynamic(() => import("./sections/TestimonialsSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const HowItWorksSection = dynamic(() => import("./sections/HowItWorksSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const FAQSection = dynamic(() => import("./sections/FAQSection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
const FinalCTASection = dynamic(() => import("./sections/FinalCTASection"), {
  ssr: false,
  loading: () => <SectionFallback />
});
interface PricingPlanProps {
  name: string;
  price: string;
  yearlyPrice?: string;
  period?: string;
  description: string;
  features: string[];
  featured?: boolean;
}
interface FeatureProps {
  icon: React.ElementType;
  title: string;
  desc: string;
}
export default function Page() {
  const [activeFeature, setActiveFeature] = useState(0);
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const handleSubscribeClick = () => {
    dispatch({
      type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
      payload: {
        ...state,
        showSubscriptionTable: !state.showSubscriptionTable
      }
    });
  };
  const CurvedDivider = ({
    color,
    flip = false
  }: {
    color: string;
    flip?: boolean;
  }) => <div className={`w-100 ${flip ? "transform rotate-180" : ""}`} data-sentry-component="CurvedDivider" data-sentry-source-file="LandingContent.tsx">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" data-sentry-element="svg" data-sentry-source-file="LandingContent.tsx">
                <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 36.7C840 27 960 13 1080 16.3C1200 20 1320 40 1380 50L1440 60V120H0V0Z" fill={color} data-sentry-element="path" data-sentry-source-file="LandingContent.tsx" />
            </svg>
        </div>;
  const features: FeatureProps[] = [{
    icon: Sparkles,
    title: "AI-Powered Image Generation",
    desc: "Create vivid character images with an advanced AI image generator designed for RPG and fantasy worlds."
  }, {
    icon: Brush,
    title: "Multiple Art Styles",
    desc: "Select from diverse art styles, including realistic, anime, and fantasy, for unique character creation."
  }, {
    icon: UserCheck,
    title: "Advanced D&D Character Creator",
    desc: "Build detailed D&D characters with custom features, backstories, and unique traits."
  }, {
    icon: Layers,
    title: "Campaign Asset Management",
    desc: "Organize all your characters, D&D monsters, and scenes in structured campaign folders."
  }, {
    icon: Users,
    title: "Community and Sharing Hub",
    desc: "Share your character creations and monster designs with a community of RPG creators."
  }, {
    icon: Settings,
    title: "Customizable Image Generation",
    desc: "Control image resolution, rendering settings, and artistic effects to perfect your designs."
  }, {
    icon: Star,
    title: "NPC and Monster Maker",
    desc: "Easily create NPCs and monsters with unique attributes and backgrounds for your campaigns including statblock generation."
  }, {
    icon: Calendar,
    title: "Daily Challenges",
    desc: "Join daily challenges to unlock rewards and explore new character creation ideas."
  }, {
    icon: Edit3,
    title: "Advanced Image Editing",
    desc: "Refine generated images with face-swapping, background edits, and upscaling features."
  }, {
    icon: Code,
    title: "Integration with D&D Beyond Character PDFs",
    desc: "Seamlessly import character sheets from popular TTRPGs and create your characters with no prompting required."
  }];
  const accentText = (text: string) => {
    // Words to emphasize
    const emphasizedWords = ["UNLIMITED", "Watermark-Free", "image editing", "editing", "FLUX.DEV Generations", "Lora", "folders", "Ultimate", "Elite", "power", "free", "2000", "4500"];
    let processedText = text;
    emphasizedWords.forEach(word => {
      const regex = new RegExp(`(${word})`, "gi");
      processedText = processedText.replace(regex,
      // eslint-disable-next-line quotes
      '<span class="bg-gradient-to-r from-sky-400 to-sky-600 text-transparent bg-clip-text font-bold">$1</span>');
    });
    return <span dangerouslySetInnerHTML={{
      __html: processedText
    }} data-sentry-component="accentText" data-sentry-source-file="LandingContent.tsx" />;
  };
  const pricingPlans: PricingPlanProps[] = [{
    name: "Free",
    price: "£0",
    yearlyPrice: "£0",
    period: "per month",
    description: "For casual users",
    features: ["10 free gold daily (10 Flux.Dev Generations)", "Limited features", "Image Generation", "Audio Generation", "Video Generation", "Take part in competitions and challenges"]
  }, {
    name: "Plus",
    price: "£9.99",
    yearlyPrice: "£109.99",
    period: "per month",
    description: "For regular users",
    features: ["900 monthly gold (900 Flux.Dev Generations)", "All art styles", "All features", "Editing Functionality", "Organise images with folders", "Save and Load Presets", "Enhanced Image Editing", "1 Free Lora Generation per month", "Watermark-Free Images"]
  }, {
    name: "Elite",
    price: "£19.99",
    yearlyPrice: "£219.99",
    period: "per month",
    description: "For power users!",
    features: ["2000 monthly gold (2000 Regular Model Generations)", "UNLIMITED FLUX.DEV GENERATIONS", "Watermark-Free Images", "All art styles", "All features", "Organise images with folders", "Save and Load Presets", "Enhanced Image Editing", "2 Free Lora Generations per month"],
    featured: true
  }, {
    name: "Ultimate",
    price: "£39.99",
    yearlyPrice: "£439.99",
    period: "per month",
    description: "For the ultimate creator!",
    features: ["4500 monthly gold (4500 Regular Model Generations)", "UNLIMITED FLUX.DEV GENERATIONS", "Watermark-Free Images", "All art styles", "All features", "Organise images with folders", "Save and Load Presets", "Enhanced Image Editing", "3 Free Lora Generations per month"]
  }];
  return <div className="dark min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden" data-sentry-component="Page" data-sentry-source-file="LandingContent.tsx">
            <main className="flex-1">
                {/* Refactored: Main Feature Section */}
                <section className="relative pt-20 md:pt-32 pb-16 md:pb-24 bg-gradient-to-b from-slate-900 via-slate-900/90 to-slate-950">
                    {/* Ensure the curve matches the color below */}
                    <div className="absolute top-0 left-0 right-0 z-0">
                        <CurvedDivider color="rgb(2, 6, 23)" flip={true} data-sentry-element="CurvedDivider" data-sentry-source-file="LandingContent.tsx" />
                    </div>
                    {/* Add subtle background elements */}
                    <div className="absolute inset-0 -z-10 opacity-10">
                        <div className="absolute bottom-0 -left-40 w-96 h-96 bg-gradient-radial from-teal-700/30 to-transparent rounded-full blur-3xl"></div>
                        <div className="absolute top-1/4 -right-40 w-96 h-96 bg-gradient-radial from-sky-700/30 to-transparent rounded-full blur-3xl"></div>
                    </div>

                    <div className="container mx-auto px-4 sm:px-6 relative z-10">
                        <div className="max-w-6xl mx-auto">
                            {/* Improved grid layout and alignment */}
                            <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                                {/* Enhanced Image Container */}
                                <div className="relative aspect-[4/3] md:aspect-square w-full overflow-hidden rounded-2xl group shadow-xl shadow-slate-950/40">
                                    <div className="absolute -inset-1 bg-gradient-to-br from-sky-600/20 to-teal-600/20 rounded-2xl blur-xl z-0 opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
                                    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-slate-700/50 group-hover:ring-sky-500/60 transition-shadow duration-300 z-10 pointer-events-none"></div>
                                    <Image src={LandingRobot} // Ensure this is imported correctly
                alt="AI-generated fantasy character artwork example" fill sizes="(max-width: 767px) calc(100vw - 2rem), 50vw" loading="lazy" // Can lazy load this now
                className="object-cover rounded-2xl transition-transform duration-500 ease-in-out group-hover:scale-105 z-0" quality={75} data-sentry-element="Image" data-sentry-source-file="LandingContent.tsx" />
                                </div>
                                {/* Prose styles adjusted for better fluid text */}
                                <div className="prose prose-invert prose-lg max-w-none text-slate-300">
                                    {/* Fluid Typography Heading */}
                                    <h2 className="text-[clamp(1.875rem,5vw,2.5rem)] font-bold leading-tight mb-6 bg-gradient-to-r from-sky-400 via-cyan-400 to-teal-500 bg-clip-text text-transparent">
                                        Create Stunning Character Art with AI
                                    </h2>
                                    {/* Fluid Typography Paragraph */}
                                    <p className="text-[clamp(1rem,2.5vw,1.125rem)] leading-relaxed mb-8">
                                        CharGen makes it simple to create
                                        stunning art for your D&D characters,
                                        NPCs, and monsters with our AI character
                                        creator. Whether you are looking for a
                                        D&D monster maker, an AI RPG generator,
                                        or the best AI image generator for your
                                        fantasy campaigns, CharGen has you
                                        covered. Experience the best D&D
                                        character creator and generate
                                        captivating fantasy AI art effortlessly.
                                    </p>
                                    {/* Modernized Tag Styling */}
                                    <div className="flex flex-wrap gap-3">
                                        {["D&D Character Creator", "Monster Maker", "NPC Creator", "Fantasy Art AI"].map(tag => <span key={tag} className="inline-flex items-center rounded-full bg-gradient-to-r from-sky-900/50 to-teal-900/50 px-4 py-1.5 text-sm font-medium text-sky-200 ring-1 ring-inset ring-sky-400/30 transition-all duration-300 hover:ring-sky-300/50 hover:text-sky-100 cursor-default">
                                                {tag}
                                            </span>)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Hero CTA Banner */}
                <CTABanner data-sentry-element="CTABanner" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="image-to-image-section" component={ImageToImageSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="features-card-grid-section" component={FeaturesCardGrid} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <section id="features" className="py-24 md:py-32 relative bg-slate-950 overflow-hidden">
                    <div className="absolute inset-0 -z-10 opacity-10">
                        <div className="absolute -top-1/4 -left-60 w-[40rem] h-[40rem] bg-gradient-radial from-cyan-800/30 to-transparent rounded-full blur-3xl"></div>
                        <div className="absolute -bottom-1/4 -right-60 w-[40rem] h-[40rem] bg-gradient-radial from-blue-800/30 to-transparent rounded-full blur-3xl"></div>
                    </div>

                    <div className="container mx-auto px-4 sm:px-6 relative z-10">
                        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
                            <h2 className="text-[clamp(2.25rem,5vw,3rem)] font-bold leading-tight mb-6 bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-500 text-transparent bg-clip-text">
                                Everything You Need
                            </h2>
                            <p className="text-[clamp(1rem,2.5vw,1.125rem)] text-slate-300 leading-relaxed">
                                Powerful features to bring your characters to
                                life
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
                            {features.map((feature, i) => <Card key={i} className={cn("rounded-xl overflow-hidden",
            // Base card styles
            "bg-slate-900/60 backdrop-blur-lg border border-slate-800/70",
            // Glassmorphism
            "cursor-pointer transform transition-all duration-300 ease-in-out",
            // Interaction base
            "hover:bg-slate-800/70 hover:border-slate-700 hover:shadow-xl hover:shadow-slate-900/20",
            // Hover state
            activeFeature === i // Active state
            ? "scale-[1.03] border-sky-600/80 shadow-lg shadow-sky-800/20" : "hover:scale-[1.02]" // Non-active hover scale
            )} onClick={() => setActiveFeature(i)}>
                                    <CardContent className="p-6">
                                        <feature.icon className={cn("h-10 w-10 mb-5 transition-colors duration-300",
                // Adjusted size/margin
                activeFeature === i ? "text-sky-400" : "text-cyan-400 group-hover:text-cyan-300" // Use group-hover if needed
                )} />
                                        <h3 className="text-lg font-semibold mb-2 text-slate-100">
                                            {feature.title}
                                        </h3>
                                        <p className="text-sm text-slate-300/90 leading-relaxed">
                                            {feature.desc}
                                        </p>
                                    </CardContent>
                                </Card>)}
                        </div>
                    </div>
                </section>

                <LazyLoadSection id="key-features-section" component={KeyFeaturesSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="rewards-section" component={RewardsSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <CTABanner data-sentry-element="CTABanner" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="ai-models-section" component={AIModelsSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="community-section" component={CommunitySection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="testimonials-section" component={TestimonialsSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="how-it-works-section" component={HowItWorksSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <CTABanner data-sentry-element="CTABanner" data-sentry-source-file="LandingContent.tsx" />

                <PricingSection pricingPlans={pricingPlans} accentText={accentText} handleSubscribeClick={handleSubscribeClick} data-sentry-element="PricingSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="faq-section" component={FAQSection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />

                <LazyLoadSection id="final-cta-section" component={FinalCTASection} fallback={<SectionFallback />} data-sentry-element="LazyLoadSection" data-sentry-source-file="LandingContent.tsx" />
            </main>
        </div>;
}